/**
 * PathEditModal — small style/delete editor for a single map path (edge).
 *
 * Opened by right-clicking a path in the builder (mirrors how double-clicking a
 * planet opens the large PlanetEditModal). Lets the admin restyle the line via
 * the shared EdgeStyleFields, or delete the path behind a JS confirm.
 *
 * A path with no stored style INHERITS the map-wide default authored in the Map
 * Settings modal, so the controls are seeded from that default (falling back to
 * DEFAULT_TRL_EDGE_STYLE) and open showing what the path actually looks like.
 * Save pins the resolved style as this path's own override; "Reset to map
 * default" drops the override so the path follows the default again.
 */

import { useState } from 'react';
import { Modal } from '@swtorbit/ui/react';
import {
  DEFAULT_TRL_EDGE_STYLE,
  resolveTrlEdgeStyle,
  type TrlEdgeStyle,
} from '@swtorbit/shared-socket-events';
import { EdgeStyleFields } from './EdgeStyleFields';

interface PathEditModalProps {
  /** The edge's own style override, or undefined when it inherits the default. */
  style?: TrlEdgeStyle;
  /** The map-wide default this path falls back to (see MapSettingsModal). */
  mapDefault?: TrlEdgeStyle;
  onConfirm: (style: TrlEdgeStyle) => void;
  /** Drop this path's override so it inherits the map default again. */
  onReset: () => void;
  onDelete: () => void;
  onCancel: () => void;
}

export function PathEditModal({
  style,
  mapDefault,
  onConfirm,
  onReset,
  onDelete,
  onCancel,
}: PathEditModalProps) {
  // Seed from what the path actually renders as today: its own override layered
  // over the map default, and the built-in look for anything neither authored.
  const [draft, setDraft] = useState<TrlEdgeStyle>(() => ({
    ...DEFAULT_TRL_EDGE_STYLE,
    ...resolveTrlEdgeStyle(style, mapDefault),
  }));

  const handleDelete = () => {
    if (window.confirm('Delete this path? This cannot be undone.')) {
      onDelete();
    }
  };

  return (
    <Modal open onClose={onCancel} className="round-creation-modal" title="Path style" footer={
<>
        <button type="button" className="btn btn--sm btn--red" onClick={handleDelete}>
          Delete path
        </button>
        <div className="d-flex gap-xs" style={{ marginLeft: 'auto' }}>
          {/* Only meaningful once the path has an override to drop. */}
          {style && (
            <button type="button" className="btn btn--outline btn--slate" onClick={onReset}>
              Reset to map default
            </button>
          )}
          <button type="button" className="btn btn--outline btn--slate" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn btn--green" onClick={() => onConfirm(draft)}>
            Save
          </button>
        </div>
      </>
}>
        <div className="d-flex flex-column gap-sm">
          {!style && (
            <p className="fs-xs text-slate m-0">
              This path currently follows the map default. Saving here gives it its own style.
            </p>
          )}
          <EdgeStyleFields value={draft} onChange={setDraft} />
        </div>
      </Modal>
  );
}
